<template>
    <div class="preorders-filter-header">
        <div
            v-if="props.showCloseBtn"
            class="close-btn"
            @click="
                () => {
                    emit('closeNavigation');
                }
            "
        >
            <i class="fal fa-close"></i>
        </div>

        <div class="date-picker">
            <ReservationDatePicker
                :minDate="minDate"
                v-model="selectedCalendarDate"
                :style="{ 'margin-left': props.showCloseBtn ? '0' : '2em' }"
            />
        </div>

        <div class="date-controls">
            <VButton class="date-control-button" @click="onPreviousDayButtonClick" :disabled="isMinDaySelected">
                <i class="far fa-chevron-left"></i>
            </VButton>
            <VButton class="date-control-button" @click="onTodayButtonClick">
                <span> {{ translations.general.today }} </span>
            </VButton>
            <VButton class="date-control-button" @click="onNextDayButtonClick">
                <i class="far fa-chevron-right"></i>
            </VButton>
        </div>

        <div class="shift-name">
            <MultiSelectFromRefCollectionInput
                v-model="selectedShiftsModel"
                :selectOptions="props.shifts"
                textProp="shiftName"
            />
        </div>
        <div v-if="false" class="table-count">
            <VButton class="table-count-button contrast">
                <span> 103 | 66 &nbsp;(156%) </span>
            </VButton>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { OperatorDao } from "@/data/dao/service/OperatorDao";
    import type { IPreorderShift } from "@/data/model/Operator";
    import { DateTime } from "@/data/util/DateTime";
    import { translations } from "@/shared/localization/translations";
    import { computed, ref, watch } from "vue";

    import VButton from "@/components/elements/VButton.vue";
    import MultiSelectFromRefCollectionInput from "@/operator/components/elements/MultiSelectFromRefCollectionInput.vue";
    import ReservationDatePicker from "@/operator/views/ReservationDatePicker.vue";

    const operatorDao = Factory.getInstance<OperatorDao>(OperatorDao);

    const selectedDateModel = defineModel<Date>("selectedDate", { required: true });
    const selectedShiftsModel = defineModel<string[]>("selectedShifts", { required: true });
    const selectedCalendarDate = ref<Date | null>(null);

    const props = defineProps<{
        shifts: IPreorderShift[];
        isLoading: boolean;
        showCloseBtn: boolean;
    }>();

    function sameDate(a: Date | null | undefined, b: Date | null | undefined) {
        return (a?.getTime() ?? null) === (b?.getTime() ?? null);
    }
    watch(
        selectedDateModel,
        (newValue) => {
            if (!sameDate(selectedCalendarDate.value, newValue)) {
                selectedCalendarDate.value = newValue;
            }
        },
        { immediate: true }
    ); // also fixes the calendar starting empty

    let requestId = 0;
    watch(selectedCalendarDate, async (newValue) => {
        if (!newValue) return;

        // Value came from the model→calendar sync above, nothing to do
        if (sameDate(newValue, selectedDateModel.value)) return;

        const id = ++requestId;

        const businessToday = await operatorDao.getPreorderBusinessToday({ localNow: newValue });
        if (id !== requestId) return;

        // Sync calendar to the canonical value so both sides agree
        if (!sameDate(selectedCalendarDate.value, businessToday)) {
            const newSelectedCalendarDate = businessToday;
            if (newSelectedCalendarDate < newValue) {
                newSelectedCalendarDate.setDate(newSelectedCalendarDate.getDate() + 1);
            }
            selectedCalendarDate.value = newSelectedCalendarDate;
        }
        if (!sameDate(selectedDateModel.value, businessToday)) {
            selectedDateModel.value = businessToday;
        }
    });

    const emit = defineEmits(["closeNavigation"]);

    const minDate = computed<Date>(() => {
        return DateTime.addDays(DateTime.getToday(), -3);
    });
    const isMinDaySelected = computed(() => {
        return DateTime.isTheSameDay(selectedDateModel.value, minDate.value) || selectedDateModel.value < minDate.value;
    });

    async function onTodayButtonClick() {
        const businessToday = await operatorDao.getPreorderBusinessToday({ localNow: new Date() });
        selectedDateModel.value = businessToday;
    }

    function onPreviousDayButtonClick() {
        selectedDateModel.value = DateTime.addDays(selectedDateModel.value, -1);
    }

    function onNextDayButtonClick() {
        selectedDateModel.value = DateTime.addDays(selectedDateModel.value, 1);
    }
</script>

<style lang="scss">
    .preorders-filter-header {
        display: flex;
        justify-content: space-between;
        gap: 10px;
        align-items: center;

        > .close-btn {
            width: 38px;
            height: 34px;
            background-color: #a6d234;
            text-align: center;
            pointer-events: all;
            margin-left: 16px;
            cursor: pointer;
            border-radius: 5px;

            > .fal {
                color: #fff;
                font-size: 22px;
                line-height: 34px;
                display: inline-block;
                position: relative;
            }
        }

        .bootstrap-select {
            width: 160px !important;

            & > .dropdown-toggle {
                padding: 0.45rem 1rem !important;
            }
        }

        .table-count-button {
            padding-left: 0.6em;
            padding-right: 0.6em;
        }

        .date-controls {
            display: flex;
            gap: 5px;
            justify-content: center;
            align-items: center;
            color: #000;

            i {
                font-size: 12px;
            }

            .v-btn.disabled,
            .v-btn[disabled] {
                background-color: #fff !important;
                border-color: #fff !important;
                color: #484848 !important;
                opacity: 0.8;

                &:hover {
                    background-color: #fff !important;
                    border-color: #fff !important;
                }
            }
        }
    }
</style>
